import { collection, deleteDoc, doc, getDoc, getDocs, query, runTransaction, setDoc, updateDoc, where } from 'firebase/firestore';
import { db } from '../config/firebase';
import roleService from './roleService';
import { ADMIN, CSF, SUPER_ADMIN, DIVISION_CONTROLLER, SALES_SUPERVISOR, TEAM_MEMBER, EXCEPTIONAL_ADMIN } from '../constants/roles';

const eventsCollection = collection(db, "events");
const eventAttendeesCollection = collection(db, "attendees");

export const EVENT_ID_TAKEN = 'event-id-taken';

const eventService = {
  // Attendees are linked to events only by ID, so an ID used by a live or deleted event must never be reused.
  isIdTaken: async (id) => {
    const [eventSnap, deletedEventSnap] = await Promise.all([
      getDoc(doc(db, "events", id)),
      getDoc(doc(db, "deleted_events", id)),
    ]);
    return eventSnap.exists() || deletedEventSnap.exists();
  },

  getAll: async () => {
    try {
      const eventsSnapshot = await getDocs(eventsCollection);
      const eventsList = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      return eventsList;
    } catch (error) {
      console.error('Error fetching events:', error);
      throw error;
    }
  },

  add: async ({ id, ...eventsData }) => {
    try {
      const eventRef = doc(db, "events", id);
      const deletedEventRef = doc(db, "deleted_events", id);

      await runTransaction(db, async (transaction) => {
        const [eventSnap, deletedEventSnap] = await Promise.all([
          transaction.get(eventRef),
          transaction.get(deletedEventRef),
        ]);
        if (eventSnap.exists() || deletedEventSnap.exists()) {
          const error = new Error(`Event ID ${id} is already in use`);
          error.code = EVENT_ID_TAKEN;
          throw error;
        }
        transaction.set(eventRef, eventsData);
      });

      return { id, ...eventsData };
    } catch (error) {
      console.error('Error adding events:', error);
      throw error;
    }
  },
  edit: async ({ id, data }) => {
    try {
      const eventsRef = doc(db, 'events', id);
      const snapshot = await getDoc(eventsRef);

      if (!snapshot.exists()) {
        throw new Error('Event not found');
      }

      await updateDoc(eventsRef, data);

    } catch (error) {
      console.error('Error updating event:', error);
      throw error;
    }
  },

  delete: async (eventId) => {
    try {
      // Get the event data before deletion
      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);

      if (eventSnap.exists()) {
        // Add to deleted_events collection with the same ID
        const deletedEventRef = doc(db, "deleted_events", eventId);
        await setDoc(deletedEventRef, {
          ...eventSnap.data(),
          id: eventId,
          deleted_at: new Date().toISOString()
        });

        // Delete from events collection
        await deleteDoc(eventRef);
        return true;
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      throw error;
    }
  },

  fetchEventsByRole: async ({ user, dateRange, eventTypeId, divisionId, teamId, spendTypeId, activityTypeId }) => {
    if (!user?.uid || !user?.role_id) return [];

    try {
      const roleName = await roleService.getNameById(user.role_id);
      if (!roleName) return [];

      let baseQuery;
      switch (roleName) {
        case ADMIN:
        case CSF:
        case SUPER_ADMIN:
        case EXCEPTIONAL_ADMIN:
          baseQuery = query(collection(db, 'events'));
          break;

        case DIVISION_CONTROLLER:
          baseQuery = query(
            collection(db, 'events'),
            where('division_id', '==', user.division_id)
          );
          break;

        case SALES_SUPERVISOR:
          baseQuery = query(
            collection(db, 'events'),
            where('team_id', '==', user.team_id)
          );
          break;

        case TEAM_MEMBER:
          baseQuery = query(collection(db, 'events'), where('created_by', '==', user.uid));
          break;

        default:
          return [];
      }

      const snapshot = await getDocs(baseQuery);
      let events = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      // Apply event type filtering if eventTypeId is provided
      if (eventTypeId) {
        events = events.filter(event => event.event_type_id === eventTypeId);
      }

      // Apply division filtering if divisionId is provided
      if (divisionId) {
        events = events.filter(event => event.division_id === divisionId);
      }

      // Apply team filtering if teamId is provided
      if (teamId) {
        events = events.filter(event => event.team_id === teamId);
      }

      // Apply spend type filtering if spendTypeId is provided
      if (spendTypeId) {
        events = events.filter(event => event.spend_type_id === spendTypeId);
      }

      // Apply activity type filtering if activityTypeId is provided
      if (activityTypeId) {
        events = events.filter(event => event.activity_type_id === activityTypeId);
      }

      // Apply date filtering if dateRange is provided
      if (dateRange?.from || dateRange?.to) {
        events = events.filter(event => {
          const eventDate = new Date(event.start_date);
          const fromDate = dateRange.from ? new Date(dateRange.from) : null;
          const toDate = dateRange.to ? new Date(dateRange.to) : null;

          if (fromDate && toDate) {
            return eventDate >= fromDate && eventDate <= toDate;
          } else if (fromDate) {
            return eventDate >= fromDate;
          } else if (toDate) {
            return eventDate <= toDate;
          }
          return true;
        });
      }

      // Sort events by start_date in descending order
      events.sort((a, b) => {
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateB - dateA;
      });

      return events;
    } catch (error) {
      console.error('Error fetching users by role:', error);
      return [];
    }
  },

  getEventDataById: async (eventId) => {
    try {
      const eventRef = doc(db, 'events', eventId);
      const eventSnapshot = await getDoc(eventRef);
      if (eventSnapshot.exists()) {
        return {
          id: eventId,
          ...eventSnapshot.data()
        };
      }
      return null;
    }
    catch (error) {
      console.error('Error fetching event data:', error);
      throw error;
    }
  },

  getEventsDataWithEveryEventTheirAttendees: async () => {
    try {
      const eventsSnapshot = await getDocs(eventsCollection);
      const eventsData = eventsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const eventsWithAttendees = await Promise.all(eventsData.map(async event => {
        const attendeesSnapshot = await getDocs(
          query(eventAttendeesCollection, where('event_id', '==', event.id))
        );

        const attendees = await Promise.all(attendeesSnapshot.docs.map(async attendeeDoc => {
          return {
            id: attendeeDoc.id,
            ...attendeeDoc.data()
          };
        }));

        return { ...event, attendees };
      }));

      return eventsWithAttendees;
    } catch (error) {
      console.error('Error fetching events with attendees:', error);
      throw error;
    }
  },

  updateEventStatus: async (eventId, status) => {
    try {
      const eventRef = doc(db, "events", eventId);
      const eventSnap = await getDoc(eventRef);

      if (!eventSnap.exists()) {
        console.warn(`Event document ${eventId} does not exist`);
        return false;
      }

      await updateDoc(eventRef, {
        status: status,
        status_updated_at: new Date().toISOString()
      });
      return true;
    } catch (error) {
      console.error('Error updating event status:', error);
      throw error;
    }
  }
};

export default eventService;
